// Test hooks: window.__snooker, for the e2e scripts and the tester's own scripts on the TEST build (?test=1).
// Drive shots without a mouse, place balls, fast-forward physics, read the state.
//
// Only installed when TEST_MODE (testFlags.ts): ?test=1 in a build that includes the hooks (REACT_APP_TEST_HOOKS=1,
// npm start, jest). Match.init is the only caller, behind `if (TEST_MODE)`; in a build without hooks TEST_MODE is the
// constant false, so the minifier drops the call and this whole module, and the bundle has no "__snooker"
// (scripts/deploy.sh checks the bundle against the environment).
//
// Keep the names: external scripts use __snooker.Match (Match.applyFrame, .shot.firstHit, .playback.frames, .concede,
// .backToMenu, .rematch, .resync, .startPractice, .canShoot, and wrap .onShotTaken), __snooker.GameStore (.toast,
// .get().searching), Balls, state(), fast(), shoot(), place(), radius, inD, camera(), frame(), cue(). view() is for
// screenshots (the table QA shots), renderInfo() for the draw-call and triangle counts (visuals measurements).
import Balls from "../Balls";
import {GameStore} from "../features/game/game-store";
import {BALL_RADIUS, inD, Positions, Vec} from "../shared/rules";
import {engine} from "./engine";
import GameState from "../GameState";
import type MatchObject from "../Match";

type Match = typeof MatchObject;

export function createTestHooks(Match: Match) {
    return {
        Match, Balls, GameStore,
        state: () => ({mode: Match.mode, phase: Match.phase, online: Match.online, frame: Match.frame, hud: GameStore.get()}),
        fast(steps: number) { Match.stepsPerFrame = Math.max(1, Math.min(100, steps | 0)); },
        /** Aim at a ball (or point) and strike, exactly as Cue.IncrementDistance does. */
        shoot(target: string | Vec, power = 0.3) {
            if (!Match.canShoot()) return false;
            const {game, cue: Cue} = engine();
            const cue = Balls.byId.cue.PhysicalBody.GetPosition();
            const t = typeof target === "string" ? Balls.byId[target].PhysicalBody.GetPosition() : {x: target[0], y: target[1]};
            const fx = t.x - cue.x, fy = t.y - cue.y;
            game.Camera.horizontalAngle = Math.atan2(-fx, -fy);
            const d = -power;
            const velocity = {x: Math.sin(game.Camera.horizontalAngle) * d * 100, y: Math.cos(game.Camera.horizontalAngle) * d * 100};
            Cue.Shot(velocity);
            Cue.SendShot(velocity);
            return true;
        },
        /** Place balls before a shot (scenario setup for tests). */
        place(positions: Positions) {
            for (const id of Object.keys(positions)) Balls.show(Balls.byId[id], positions[id]);
        },
        radius: BALL_RADIUS,
        inD,
        camera: () => ({h: engine().game.Camera.horizontalAngle, v: engine().game.Camera.verticalAngle}),
        /** Point the camera (angles as in camera(), distance from the cue ball) for screenshots; the next frame uses it. */
        view(v: {h?: number, v?: number, distance?: number}) {
            const cam = engine().game.Camera;
            if (typeof v.h === "number") cam.horizontalAngle = v.h;
            if (typeof v.v === "number") cam.verticalAngle = v.v;
            if (typeof v.distance === "number") GameState.cameraDistance = v.distance;
        },
        // a harness that feeds synthetic event timestamps in one go can mark rendered frames itself (no frame = browser stall)
        frame: (t: number) => engine().cue.Tracker.frame(t),
        /** The last frame's renderer.info (draw calls, triangles) and the graphics quality level in use. */
        renderInfo: () => {
            const game = engine().game as any, r = game.Resources && game.Resources.renderer;
            const info = r && r.info ? r.info.render : null;
            return {calls: info ? info.calls : null, faces: info ? info.faces : null, vertices: info ? info.vertices : null,
                quality: game.Quality ? game.Quality.level : null, pixelRatio: r ? r.devicePixelRatio : null};
        },
        cue: () => {
            const Cue = engine().cue;
            return {distance: Cue.Distance, pull: Cue.Tracker.pull, armed: Cue.Tracker.armed, active: Cue.StrokeActive, blocked: Cue.Tracker.blocked(performance.now())};
        },
    };
}

export type TestHooks = ReturnType<typeof createTestHooks>;

declare global {
    interface Window { __snooker?: TestHooks }
}

/** Match.init, only `if (TEST_MODE)`. */
export function installTestHooks(Match: Match) {
    window.__snooker = createTestHooks(Match);
}
